import { supabase } from '../lib/supabase';

const isVisibleUnitRegistration = registration => {
  const status = String(registration?.enrollment_status || '').toLowerCase();
  const review = String(registration?.review_status || '').toLowerCase();
  const resolution = String(registration?.resolution_type || '').toLowerCase();
  if (registration?.is_active === false) return false;
  if (['withdrawn', 'removed', 'rejected', 'cancelled', 'deleted'].includes(status)) return false;
  if (['wrong_unit', 'already_completed', 'credit_transferred', 'elective_not_selected'].includes(review)) return false;
  if (['wrong_unit', 'already_completed', 'credit_transfer', 'credit_transferred', 'elective_not_selected'].includes(resolution)) return false;
  // Waiting-list rows still belong in academic-management counts/reports;
  // callers that need teaching-ready units can restrict to `enrolled`.
  return !status || ['enrolled', 'active', 'completed', 'waiting'].includes(status);
};

const ok = data => ({ success: true, data });
const fail = error => ({ success: false, error: error?.message || String(error) });
const levelNumber = value => Number(String(value || '').match(/[3-7]/)?.[0]);

export const getEligibleStudents = async () => {
  const { data, error } = await supabase.rpc('get_scoped_enrollment_students');
  if (error) throw error;
  return (data || []).map(row => row.student || row);
};

export const getStudentProgramEnrollment = async studentId => {
  try {
    const { data, error } = await supabase.from('students').select('profile_id,program_id,academic_level,current_module,starting_module,status,program:program_id(id,name,code)').eq('profile_id', studentId).single();
    if (error) throw error;
    return ok({ id:data.profile_id, program_id:data.program_id, level_id:levelNumber(data.academic_level), module_number:Number(data.current_module || data.starting_module || 1), status:data.status, program:data.program });
  } catch (error) { return fail(error); }
};

export const getActiveEnrollmentContext = async () => {
  try {
    const { error: statusError } = await supabase.rpc('update_academic_calendar_status');
    if (statusError) console.warn('Academic calendar status refresh was skipped:', statusError.message);
    const { data: year, error: yearError } = await supabase.from('academic_years').select('*').ilike('status','active').order('start_date',{ascending:false}).limit(1).maybeSingle();
    if (yearError) throw yearError;
    if (!year) return ok({ academicYear:null, semester:null });
    const { data: semester, error: semesterError } = await supabase.from('semesters').select('*').eq('academic_year_id',year.id).ilike('status','active').order('start_date',{ascending:false}).limit(1).maybeSingle();
    if (semesterError) throw semesterError;
    return ok({ academicYear:year, semester:semester || null });
  } catch (error) { return fail(error); }
};

export const getSemesterEnrollment = async (studentId, academicYearId, semesterId) => {
  const { data, error } = await supabase.from('student_semester_enrollments').select('*').eq('student_id',studentId).eq('academic_year_id',academicYearId).eq('semester_id',semesterId).maybeSingle();
  if (error) throw error;
  return data;
};

// Result release advances a learner's module on the server.  Calling this on
// registration load also repairs enrollments created before the final batch
// was released (for example, an active Module 1 enrollment waiting for the
// Module 2 transition).
export const syncStudentModuleProgression = async studentId => {
  const { data, error } = await supabase.rpc('sync_student_module_progression', { target_student: studentId });
  if (error) return fail(error);
  return ok(data);
};

export const reportForSemester = async (studentId, academicYearId, semesterId) => {
  const progression = await syncStudentModuleProgression(studentId);
  if (!progression.success) console.warn('Module progression sync was skipped:', progression.error);
  const { data, error } = await supabase.rpc('report_for_semester',{target_student:studentId,target_year:academicYearId,target_semester:semesterId});
  if (error) throw error;
  return ok(data);
};

export const activateSemesterEnrollment = async enrollmentId => {
  const { data, error } = await supabase.rpc('activate_semester_enrollment',{target_enrollment:enrollmentId});
  if (error) throw error;
  return ok(data);
};

export const reactivateSemesterEnrollment = async (enrollmentId, reason) => {
  const { data, error } = await supabase.rpc('reactivate_semester_enrollment',{target_enrollment:enrollmentId,reason});
  if (error) throw error;
  return ok(data);
};

export const updateEnrollmentWindow = async (semesterId, settings) => {
  const payload = {
    reporting_opens_at:settings.reporting_opens_at || null,
    reporting_closes_at:settings.reporting_closes_at || null,
    registration_opens_at:settings.registration_opens_at || null,
    registration_closes_at:settings.registration_closes_at || null,
    late_registration_closes_at:settings.late_registration_closes_at || null,
    // Kept at zero for compatibility; penalties are always stored separately.
    late_registration_fee:0,
    late_reporting_fee:settings.late_reporting_fee === '' ? null : Number(settings.late_reporting_fee || 0),
    late_unit_registration_fee:settings.late_unit_registration_fee === '' ? null : Number(settings.late_unit_registration_fee || 0),
    allow_late_reporting:settings.allow_late_reporting,
    allow_late_unit_registration:settings.allow_late_unit_registration,
    reactivation_fee:Number(settings.reactivation_fee || 0)
  };
  const { data, error } = await supabase.from('semesters').update(payload).eq('id',semesterId).select().single();
  if (error) throw error;
  return data;
};

export const completeUnitRegistration = async enrollmentId => {
  const { data, error } = await supabase.rpc('complete_unit_registration',{target_enrollment:enrollmentId});
  if (error) throw error;
  return ok(data);
};

export const getAvailableUnits = async (programId, levelId, semesterId, moduleNumber) => {
  try {
    let query=supabase.from('units').select('*').eq('program_id',programId).eq('is_active',true);
    const level=levelNumber(levelId); if(level) query=query.eq('program_level',level);if(moduleNumber)query=query.eq('module_in_level',Number(moduleNumber));
    const {data:units,error}=await query.order('module_in_level').order('code'); if(error) throw error;
    const ids=(units||[]).map(u=>String(u.id)); let counts={};
    if(ids.length&&semesterId){const {data,error:countError}=await supabase.from('student_unit_registrations').select('unit_id,enrollment_status').eq('semester_id',semesterId).in('unit_id',ids);if(countError)throw countError;counts=(data||[]).reduce((a,r)=>{if(r.enrollment_status==='enrolled')a[r.unit_id]=(a[r.unit_id]||0)+1;return a;},{});}
    return ok((units||[]).map(u=>({...u,unit_code:u.code,unit_name:u.name,credits:u.credit_hours||0,current_enrollment:counts[String(u.id)]||0,max_capacity:u.registration_capacity||null,prerequisites:[]})));
  } catch(error){return fail(error);}
};

export const getStudentEnrollments = async (studentId, academicYearId, semesterId) => {
  try {
    const {data:rows,error}=await supabase.from('student_unit_registrations').select('*').eq('student_id',studentId).eq('academic_year_id',academicYearId).eq('semester_id',semesterId).order('enrolled_at');if(error)throw error;
    const ids=[...new Set((rows||[]).map(r=>String(r.unit_id||'')).filter(Boolean))];let map={};
    if(ids.length){
      // Registrations are stored as TEXT for legacy code-based imports, while
      // units.id is UUID. Split the lookup so a unit code never gets sent to
      // a UUID filter (which causes a Postgres operator/type error).
      const uuidPattern=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const idValues=ids.filter(value=>uuidPattern.test(value));
      const codeValues=ids.filter(value=>!uuidPattern.test(value));
      const [byId,byCode]=await Promise.all([
        idValues.length?supabase.from('units').select('*').in('id',idValues):Promise.resolve({data:[],error:null}),
        codeValues.length?supabase.from('units').select('*').in('code',codeValues):Promise.resolve({data:[],error:null})
      ]);
      if(byId.error)throw byId.error;if(byCode.error)throw byCode.error;
      map=Object.fromEntries([...(byId.data||[]),...(byCode.data||[])].map(u=>[String(u.id),{...u,unit_code:u.code,unit_name:u.name,credits:u.credit_hours}]));
      const byCodeKey=Object.fromEntries((byCode.data||[]).map(u=>[String(u.code),{...u,unit_code:u.code,unit_name:u.name,credits:u.credit_hours}]));
      (byCode.data||[]).forEach(unit=>{map[String(unit.code)]=byCodeKey[String(unit.code)];});
    }
    return ok((rows||[]).map(r=>({...r,unit:map[r.unit_id]||null})));
  }catch(error){return fail(error);}
};

export const validatePrerequisites = async () => ok({is_valid:true,missing_prerequisites:[]});

export const checkUnitCapacity = async unitId => {
  try{const {data:unit,error}=await supabase.from('units').select('registration_capacity').eq('id',unitId).single();if(error)throw error;const {count,error:countError}=await supabase.from('student_unit_registrations').select('id',{count:'exact',head:true}).eq('unit_id',String(unitId)).eq('enrollment_status','enrolled');if(countError)throw countError;const cap=unit.registration_capacity;return ok({has_capacity:cap==null||count<cap,current_enrollment:count||0,max_capacity:cap,remaining:cap==null?null:Math.max(cap-count,0)});}catch(error){return fail(error);}
};

export const enrollStudentInUnit = async payload => {
  try{const {data,error}=await supabase.rpc('register_student_unit',{target_student:payload.studentId,target_year:payload.academicYearId,target_semester:payload.semesterId,target_unit:String(payload.unitId)});if(error)throw error;return ok({...data,message:data.enrollment_status==='waiting'?'Added to waiting list':'Unit registered'});}catch(error){return fail(error);}
};

export const getEnrollmentStatistics = async (academicYearId,semesterId) => {
  let query=supabase.from('student_unit_registrations').select('unit_id,enrollment_status,is_active,review_status,resolution_type');if(academicYearId)query=query.eq('academic_year_id',academicYearId);if(semesterId)query=query.eq('semester_id',semesterId);const {data,error}=await query;if(error)throw error;const visible=(data||[]).filter(isVisibleUnitRegistration);return{total:visible.length,enrolled:visible.filter(x=>x.enrollment_status==='enrolled').length,waiting:visible.filter(x=>x.enrollment_status==='waiting').length,uniqueUnits:new Set(visible.map(x=>x.unit_id)).size};
};

export const getSemesterEnrollments = async (academicYearId,semesterId) => {
  let query=supabase.from('student_semester_enrollments').select('*').order('reported_at',{ascending:false});if(academicYearId)query=query.eq('academic_year_id',academicYearId);if(semesterId)query=query.eq('semester_id',semesterId);const {data,error}=await query;if(error)throw error;return data||[];
};

export const getEnrollmentDashboard = async (academicYearId,semesterId) => {
  const [enrollments,unitResult,billingResult] = await Promise.all([
    getSemesterEnrollments(academicYearId,semesterId),
    supabase.from('student_unit_registrations').select('student_id,unit_id,enrollment_status,is_active,review_status,resolution_type').eq('academic_year_id',academicYearId).eq('semester_id',semesterId),
    supabase.from('student_billing_requests').select('student_id,status,late_reporting_fee,late_unit_registration_fee,reactivation_fee').eq('academic_year_id',academicYearId).eq('semester_id',semesterId)
  ]);
  if(unitResult.error)throw unitResult.error;if(billingResult.error)throw billingResult.error;
  const visibleUnitRows=(unitResult.data||[]).filter(isVisibleUnitRegistration);
  const unitsByStudent=visibleUnitRows.reduce((a,r)=>{if(!a[r.student_id])a[r.student_id]=[];a[r.student_id].push(r);return a;},{});
  const billingByStudent=Object.fromEntries((billingResult.data||[]).map(r=>[r.student_id,r]));
  return {enrollments,unitsByStudent,billingByStudent,stats:{reported:enrollments.filter(e=>e.status==='reported').length,active:enrollments.filter(e=>e.status==='active').length,completed:enrollments.filter(e=>['registration_completed','billed'].includes(e.status)).length,billingPending:(billingResult.data||[]).filter(r=>r.status==='pending').length,registeredUnits:visibleUnitRows.filter(r=>r.enrollment_status==='enrolled').length}};
};

export const exportEnrollmentReport = async filters => {
  try{let query=supabase.from('student_unit_registrations').select('*');if(filters?.academicYearId)query=query.eq('academic_year_id',filters.academicYearId);if(filters?.semesterId)query=query.eq('semester_id',filters.semesterId);const {data:rawRows,error}=await query.order('enrolled_at');if(error)throw error;const rows=(rawRows||[]).filter(isVisibleUnitRegistration);const sids=[...new Set(rows.map(r=>r.student_id))],uids=[...new Set(rows.map(r=>r.unit_id))];const [sr,ur]=await Promise.all([sids.length?supabase.from('students').select('profile_id,admission_number,first_name,middle_name,last_name').in('profile_id',sids):Promise.resolve({data:[]}),uids.length?supabase.from('units').select('id,code,name,credit_hours').in('id',uids):Promise.resolve({data:[]})]);const sm=Object.fromEntries((sr.data||[]).map(s=>[s.profile_id,{student_id:s.admission_number,full_name:[s.first_name,s.middle_name,s.last_name].filter(Boolean).join(' ')}])),um=Object.fromEntries((ur.data||[]).map(u=>[String(u.id),{unit_code:u.code,unit_name:u.name,credits:u.credit_hours}]));return ok(rows.map(r=>({...r,student:sm[r.student_id],unit:um[r.unit_id],academic_year:{year_name:filters?.academicYearName||''},semester:{semester_name:filters?.semesterName||''}})));}catch(error){return fail(error);}
};

export const withdrawFromUnit = async (id,reason) => {const {data,error}=await supabase.from('student_unit_registrations').update({enrollment_status:'withdrawn',withdrawn_at:new Date().toISOString(),withdrawal_reason:reason}).eq('id',id).in('enrollment_status',['enrolled','waiting']).select().single();return error?fail(error):ok(data);};

// HOD/academic-manager exception handling. The database RPC records the
// decision and keeps the registration row for audit instead of deleting it.
export const resolveStudentUnitRegistration = async (registrationId, resolutionType, notes = '') => {
  try {
    const { data, error } = await supabase.rpc('resolve_student_unit_registration', {
      target_registration: registrationId,
      target_resolution: resolutionType,
      resolution_notes: notes || null,
    });
    if (error) throw error;
    return ok(data);
  } catch (error) { return fail(error); }
};

const bulkBatches=new Map();
export const createBulkEnrollmentBatch=async data=>{const id=crypto.randomUUID();bulkBatches.set(id,data);return ok({id});};
export const processBulkEnrollment=async (batchId,studentIds,unitIds,academicYearId,semesterId)=>{const batch=bulkBatches.get(batchId);if(!batch)return fail('Bulk enrolment batch expired. Please restart.');let successful=0;const failures=[];for(const studentId of studentIds||batch.studentIds||[])for(const unitId of unitIds||batch.unitIds||[]){const result=await enrollStudentInUnit({studentId,unitId,academicYearId:academicYearId||batch.academicYearId,semesterId:semesterId||batch.semesterId});if(result.success)successful+=1;else failures.push({studentId,unitId,error:result.error});}bulkBatches.delete(batchId);return ok({total_processed:successful+failures.length,successful,failed:failures.length,failures});};

export const getInstitutionEnrollmentSettings = async () => {
  const { data, error } = await supabase
    .from('institution_settings')
    .select('enrollment_controls_enabled,require_configured_enrollment_windows,require_intake_eligibility,allow_late_reporting,allow_late_unit_registration,default_late_reporting_fee,default_late_unit_registration_fee,default_reactivation_fee,reporting_opens_at,reporting_closes_at,registration_opens_at,registration_closes_at,late_registration_closes_at,enrollment_window_academic_year_id,enrollment_window_semester_id')
    .limit(1)
    .single();
  if (error) throw error;
  return data;
};

export const updateInstitutionEnrollmentSettings = async (settings) => {
  const { data, error } = await supabase
    .from('institution_settings')
    .update({
      enrollment_controls_enabled: settings.enrollment_controls_enabled,
      require_configured_enrollment_windows: settings.require_configured_enrollment_windows,
      require_intake_eligibility: settings.require_intake_eligibility,
      allow_late_reporting: settings.allow_late_reporting,
      allow_late_unit_registration: settings.allow_late_unit_registration,
      default_late_reporting_fee: Number(settings.default_late_reporting_fee || 0),
      default_late_unit_registration_fee: Number(settings.default_late_unit_registration_fee || 0),
      default_reactivation_fee: Number(settings.default_reactivation_fee || 0),
      reporting_opens_at: settings.reporting_opens_at || null,
      reporting_closes_at: settings.reporting_closes_at || null,
      registration_opens_at: settings.registration_opens_at || null,
      registration_closes_at: settings.registration_closes_at || null,
      late_registration_closes_at: settings.late_registration_closes_at || null,
      enrollment_window_academic_year_id: settings.enrollment_window_academic_year_id || null,
      enrollment_window_semester_id: settings.enrollment_window_semester_id || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
};
